/**
 * Patient Flow PDF Generator
 *
 * Generates a PDF for the label "Patient Flow" tab (src/tags/LabelPatientFlow.js)
 * that mirrors the on-screen visualization: rolled-up label rows pinned at the
 * top, then per-provider rows, each with a horizontal bar sized by shared
 * patients and colored by its label.
 *
 * Reuses the existing Doctivity gradient-header template
 * (drawPageHeader / drawContinuationPage / loadDoctivityLogo).
 *
 * The caller (component) supplies a fully-resolved render model — bar widths,
 * colors, formatted percentages — so this module stays pure drawing and does
 * not need the app's color/scale utilities.
 */

import { jsPDF } from 'jspdf';
import { format } from 'date-fns';
import { registerRobotoFont } from './robotoFont';
import { drawPageHeader, drawContinuationPage } from './chartRenderer';
import { loadDoctivityLogo } from './logoManager';

// ─── Layout constants ─────────────────────────────────────────────────────────
const LOGO_MAX_W = 90;
const LOGO_MAX_H = 22;
const LOGO_ASPECT = 261 / 59;
const CONTENT_L = 20;
const CONTENT_R = 20;
const FOOTER_RESERVE = 40;
const ROW_H = 16;
const BAR_H = 9;

const HEAD_BG = [235, 241, 248]; // #EBF1F8 — on-screen header row
const HEAD_TXT = [100, 116, 139]; // #64748B
const COLLAPSED_BG = [238, 246, 244]; // #EEF6F4 — on-screen collapsed row tint
const ZEBRA_BG = [248, 250, 252]; // #F8FAFC — on-screen even provider row
const NAME_TXT = [33, 37, 41]; // #212529
const SUB_TXT = [75, 85, 99]; // #4B5563
const NEUTRAL_BAR = [203, 213, 225]; // #CBD5E1

// ─── Material Icons glyph rasterizer ──────────────────────────────────────────
// Rasterize a Material Icons glyph to a PNG via the webfont already loaded by
// the app, so PDF markers match the on-screen icons ('hub' = affiliated
// provider #5391D5; 'sell' = rolled-up label, tinted its label color). Cached
// per glyph+color. Mirrors loadHubIcon in locationPdfGenerator.
const iconCache = {};
const loadMaterialIcon = async (glyph, color, size = 32) => {
  const cacheKey = `${glyph}:${color}`;
  if (cacheKey in iconCache) return iconCache[cacheKey];
  let dataUrl = null;
  try {
    await document.fonts.ready;
    const scale = 2; // render at 2× for a crisp PDF image
    const canvas = document.createElement('canvas');
    canvas.width = size * scale;
    canvas.height = size * scale;
    const ctx = canvas.getContext('2d');
    ctx.font = `${size * scale}px "Material Icons"`;
    ctx.fillStyle = color;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.fillText(glyph, (size * scale) / 2, (size * scale) / 2);
    dataUrl = canvas.toDataURL('image/png');
  } catch (e) {
    dataUrl = null;
  }
  iconCache[cacheKey] = dataUrl;
  return dataUrl;
};

/** #RRGGBB → [r,g,b]; falls back to neutral grey on bad input. */
const hexToRgb = (hex) => {
  if (typeof hex === 'string') {
    const m = hex.replace('#', '');
    if (m.length === 6) {
      const n = parseInt(m, 16);
      if (!Number.isNaN(n)) return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
  }
  return NEUTRAL_BAR;
};

/** Place the cached logo at the top-right corner of the current page. */
const placeLogoOnPage = (doc, logo, pageWidth) => {
  if (!logo) return;
  let w = LOGO_MAX_W;
  let h = w / LOGO_ASPECT;
  if (h > LOGO_MAX_H) { h = LOGO_MAX_H; w = h * LOGO_ASPECT; }
  const lx = pageWidth - 15 - LOGO_MAX_W;
  doc.addImage(
    `data:image/${logo.type.toLowerCase()};base64,${logo.data}`,
    logo.type,
    lx, 5, w, h,
  );
};

/** Ellipsize `text` to fit `maxW` points at the current font settings. */
const fit = (doc, text, maxW) => {
  let s = String(text ?? '');
  if (doc.getTextWidth(s) <= maxW) return s;
  while (s.length > 1 && doc.getTextWidth(`${s}…`) > maxW) s = s.slice(0, -1);
  return `${s}…`;
};

/**
 * Build the jsPDF document for the patient flow visualization.
 *
 * @param {Object}   opts
 * @param {string}   opts.title
 * @param {string}   [opts.subtitle]
 * @param {boolean}  opts.showProviderCols - include Specialty/Organization columns
 * @param {Array}    opts.rows - render model; each:
 *   { type: 'collapsed'|'provider', affiliated, label, labelColor, name,
 *     specialty, org, pctLabel, barPct (0-100), barColor }
 * @returns {Promise<jsPDF>}
 */
export const generatePatientFlowPDF = async ({
  title,
  subtitle = '',
  showProviderCols = true,
  rows = [],
}) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
  registerRobotoFont(doc);

  const pageWidth = doc.internal.pageSize.width; // 792
  const pageHeight = doc.internal.pageSize.height; // 612

  const logo = await loadDoctivityLogo();
  const placeLogo = () => placeLogoOnPage(doc, logo, pageWidth);

  // ── Column geometry ────────────────────────────────────────────────────────
  const lx = CONTENT_L;
  const totalW = pageWidth - CONTENT_L - CONTENT_R;
  const cols = {
    icon: 14,
    label: 88,
    name: 130,
    specialty: showProviderCols ? 84 : 0,
    org: showProviderCols ? 110 : 0,
    patients: 14,
    pct: 42,
  };
  const fixed = cols.icon + cols.label + cols.name + cols.specialty
    + cols.org + cols.patients + cols.pct;
  const barW = Math.max(120, totalW - fixed);

  // X offset of each column
  const xIcon = lx;
  const xLabel = xIcon + cols.icon;
  const xName = xLabel + cols.label;
  const xSpecialty = xName + cols.name;
  const xOrg = xSpecialty + cols.specialty;
  const xPatients = xOrg + cols.org;
  const xPct = xPatients + cols.patients;
  const xBar = xPct + cols.pct;

  const drawColumnHeader = (y) => {
    doc.setFillColor(...HEAD_BG);
    doc.rect(lx, y, totalW, ROW_H, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...HEAD_TXT);
    const ty = y + 11;
    doc.text('LABEL', xLabel + 2, ty);
    doc.text('PROVIDER', xName + 2, ty);
    if (showProviderCols) {
      doc.text('SPECIALTY', xSpecialty + 2, ty);
      doc.text('ORGANIZATION', xOrg + 2, ty);
    }
    doc.text('SHARED PATIENTS', xPatients + 2, ty);
    return y + ROW_H + 2;
  };

  // ── Page 1 header ──────────────────────────────────────────────────────────
  const { y: contentY } = drawPageHeader(doc, title || 'Patient Flow', subtitle, [], {
    pdfType: 'printable',
  });
  placeLogo();
  let y = drawColumnHeader(contentY);

  if (rows.length === 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text('No patient flow data for the current filters.', lx + 4, y + 16);
    return doc;
  }

  // Pre-render glyphs once so the row loop can draw them synchronously:
  // the affiliated 'hub' icon, and a 'sell' (tag) icon per distinct collapsed
  // label color (the icon is tinted its label's color on screen).
  const hubIcon = await loadMaterialIcon('hub', '#5391D5');
  const sellColors = [
    ...new Set(
      rows
        .filter((r) => r.type === 'collapsed')
        .map((r) => r.labelColor || '#00a887'),
    ),
  ];
  const sellIcons = Object.fromEntries(
    await Promise.all(
      sellColors.map(async (c) => [c, await loadMaterialIcon('sell', c)]),
    ),
  );
  // Grey patient-count glyphs shown just left of the bar: 'person' per provider,
  // 'group' per rolled-up label row (on-screen color #9CA3AF).
  const [personIcon, groupIcon] = await Promise.all([
    loadMaterialIcon('person', '#9CA3AF'),
    loadMaterialIcon('group', '#9CA3AF'),
  ]);

  let providerIndex = 0;
  rows.forEach((row) => {
    // Page break
    if (y + ROW_H > pageHeight - FOOTER_RESERVE) {
      doc.addPage();
      y = drawContinuationPage(doc, { pdfType: 'printable' });
      placeLogo();
      y = drawColumnHeader(y);
    }

    const isCollapsed = row.type === 'collapsed';

    // Row background (collapsed tint / provider zebra)
    if (isCollapsed) {
      doc.setFillColor(...COLLAPSED_BG);
      doc.rect(lx, y, totalW, ROW_H, 'F');
    } else {
      if (providerIndex % 2 === 1) {
        doc.setFillColor(...ZEBRA_BG);
        doc.rect(lx, y, totalW, ROW_H, 'F');
      }
      providerIndex += 1;
    }

    const midY = y + ROW_H / 2;
    const textY = y + 11;

    // Icon: the Material 'sell' (tag) glyph for rolled-up label rows, the 'hub'
    // glyph for affiliated providers — matching the on-screen markers. Falls
    // back to a colored swatch if the glyph couldn't be rasterized.
    if (isCollapsed) {
      const sellIcon = sellIcons[row.labelColor || '#00a887'];
      if (sellIcon) {
        const s = 10;
        doc.addImage(sellIcon, 'PNG', xIcon + 2, midY - s / 2, s, s);
      } else {
        doc.setFillColor(...hexToRgb(row.barColor));
        doc.rect(xIcon + 2, midY - 4, 8, 8, 'F');
      }
    } else if (row.affiliated && hubIcon) {
      const s = 10;
      doc.addImage(hubIcon, 'PNG', xIcon + 2, midY - s / 2, s, s);
    }

    // Label (colored)
    doc.setFont('helvetica', isCollapsed ? 'bold' : 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...(row.labelColor ? hexToRgb(row.labelColor) : SUB_TXT));
    doc.text(fit(doc, row.label, cols.label - 4), xLabel + 2, textY);

    // Provider name (or "N providers" for collapsed)
    doc.setFont('helvetica', isCollapsed ? 'bold' : 'normal');
    doc.setTextColor(...(isCollapsed && row.labelColor ? hexToRgb(row.labelColor) : NAME_TXT));
    doc.text(fit(doc, row.name, cols.name - 4), xName + 2, textY);

    // Specialty / Organization
    if (showProviderCols) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(...SUB_TXT);
      if (row.specialty) doc.text(fit(doc, row.specialty, cols.specialty - 4), xSpecialty + 2, textY);
      if (row.org) doc.text(fit(doc, row.org, cols.org - 4), xOrg + 2, textY);
    }

    // Patient-count glyph just left of the bar ('group' for rolled-up rows,
    // 'person' for providers).
    const patientIcon = isCollapsed ? groupIcon : personIcon;
    if (patientIcon) {
      const s = 9;
      doc.addImage(patientIcon, 'PNG', xPatients + 2, midY - s / 2, s, s);
    }

    // Percent
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...SUB_TXT);
    if (row.pctLabel) doc.text(row.pctLabel, xPct + 2, textY);

    // Bar
    const pct = Math.max(0, Math.min(100, row.barPct || 0));
    if (pct > 0) {
      const w = Math.max(1.5, (pct / 100) * barW);
      doc.setFillColor(...hexToRgb(row.barColor));
      doc.roundedRect(xBar, midY - BAR_H / 2, w, BAR_H, 1.5, 1.5, 'F');
    }

    y += ROW_H;
  });

  return doc;
};

/**
 * Generate the PDF and trigger a browser download.
 */
export const downloadPatientFlowPDF = async ({
  title,
  subtitle,
  showProviderCols,
  rows,
  filenameBase = 'patient_flow',
}) => {
  const doc = await generatePatientFlowPDF({ title, subtitle, showProviderCols, rows });

  const safeName = filenameBase.replace(/[^a-zA-Z0-9]/g, '_');
  const dateSuffix = format(new Date(), 'yy_MM_dd');
  doc.save(`doctivity_${safeName}_${dateSuffix}.pdf`);
};

export default {
  generatePatientFlowPDF,
  downloadPatientFlowPDF,
};
